// 서비스 워커 등록·업데이트 알림·설치 안내(브라우저 연결부). 게임 진행·localStorage 저장·계정 동기화(src/account/**)는 건드리지 않는다.
//  - 등록은 인트로가 걷힌 뒤(또는 10초 뒤): 첫 화면 자원에 회선을 준다.
//  - 새 버전: 새 워커가 설치돼 기다리면(waiting) 새 표를 받아 "받아야 할 양"을 계산하고 상태만 바꾼다. 화면(마을 띠)이 이 상태를 읽는다.
//    "지금 적용"은 마을에서 사용자가 눌렀을 때만 새로고침한다(전투 중 강제 새로고침 없음). 누르지 않으면 다음 접속에 저절로 적용된다.
import { manifestEntries } from './assetMap';
import { planUpdate, type ShellFile } from './patch';
import { holdRegionData, queuePatch } from './regionData';

export interface PwaState {
  /** 서비스 워커를 쓸 수 있는 환경(보안 연결 + 지원) */
  supported: boolean;
  registered: boolean;
  controlled: boolean;
  /** 새 버전이 설치돼 기다리는 중 */
  updateReady: boolean;
  /** 적용 뒤 받게 될 양(바이트, 이 기기에 이미 받아 둔 파일 기준) */
  updateBytes: number;
  /** 홈 화면 앱으로 실행 중 */
  installed: boolean;
  /** 브라우저가 설치 창을 열 수 있다(beforeinstallprompt) */
  canInstall: boolean;
  ios: boolean;
  /** 인스타·카톡 같은 앱 안 브라우저(설치 불가) */
  inApp: boolean;
}

const state: PwaState = { supported: false, registered: false, controlled: false, updateReady: false, updateBytes: 0, installed: false, canInstall: false, ios: false, inApp: false };
const listeners = new Set<() => void>();
const emit = (): void => { for (const f of listeners) f(); };
export const pwaState = (): Readonly<PwaState> => state;
export function onPwaChange(fn: () => void): () => void { listeners.add(fn); return () => listeners.delete(fn); }

/** 이 기능의 기기 전용 저장 키(LOCAL_ONLY_KEYS 에 분류) */
export const PWA_KEY = 'dungeonhero.pwa.v1';
export interface PwaPrefs { hintSeen?: boolean; bandDismissed?: string }
export function loadPwaPrefs(): PwaPrefs {
  try { const raw = localStorage.getItem(PWA_KEY); return raw ? (JSON.parse(raw) as PwaPrefs) : {}; } catch { return {}; }
}
export function savePwaPrefs(p: PwaPrefs): void {
  try { localStorage.setItem(PWA_KEY, JSON.stringify(p)); } catch { /* 저장소 막힘: 이번 접속에서만 */ }
}

interface InstallPromptEvent extends Event { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }
let installEvent: InstallPromptEvent | null = null;
let reg: ServiceWorkerRegistration | null = null;
let applying = false;
let hooks: { isSafe: () => boolean; flush: () => void } = { isSafe: () => true, flush: () => {} };

function detectInstalled(): boolean {
  try {
    const nav = navigator as Navigator & { standalone?: boolean };
    return nav.standalone === true || matchMedia('(display-mode: standalone)').matches || matchMedia('(display-mode: fullscreen)').matches;
  } catch { return false; }
}

async function computeUpdate(): Promise<void> {
  try {
    const r = await fetch('/asset-manifest.json', { cache: 'no-store' });
    if (!r.ok) return;
    const j = (await r.json()) as { f?: Record<string, string>; shell?: ShellFile[] };
    let cached = new Set<string>();
    if (typeof caches !== 'undefined') {
      const c = await caches.open('dh-assets-v1');
      cached = new Set((await c.keys()).map((q) => new URL(q.url).pathname));
    }
    // 지금 실행 중인 셸 번들(문서에 붙은 script·link): 이름이 같은 번들은 다시 받지 않는다
    const running = new Set<string>();
    document.querySelectorAll('script[src],link[href]').forEach((n) => {
      const u = n.getAttribute('src') ?? n.getAttribute('href') ?? '';
      if (/assets\/[^/]+\.(?:js|css)$/.test(u)) running.add(new URL(u, location.href).pathname);
    });
    const plan = planUpdate(manifestEntries(), j.f ?? {}, j.shell ?? [], cached, running);
    state.updateBytes = plan.total;
  } catch { /* 크기를 못 구해도 알림은 띄운다(0 이면 크기 없이 안내) */ }
}

function watch(r: ServiceWorkerRegistration): void {
  const check = (): void => {
    if (r.waiting && navigator.serviceWorker.controller) {
      void computeUpdate().then(() => { state.updateReady = true; emit(); });
    }
  };
  check();
  r.addEventListener('updatefound', () => {
    const w = r.installing;
    if (!w) return;
    w.addEventListener('statechange', () => { if (w.state === 'installed') check(); });
  });
  // 오래 켜 둔 탭도 새 버전을 알아보게 가끔 확인(워커 바이트가 같으면 아무 일도 없다)
  const poll = (): void => { void r.update().catch(() => {}); };
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') poll(); });
  setInterval(poll, 30 * 60 * 1000);
}

/** 서비스 워커가 활성화 때 남긴 "바뀐 파일" 목록을 읽어 한가할 때 다시 받게 한다(패치) */
async function readPatchTodo(): Promise<void> {
  try {
    const meta = await caches.open('dh-meta-v1');
    const res = await meta.match('/__patch-todo');
    if (!res) return;
    const j = (await res.json()) as { rels?: string[] };
    if (j.rels && j.rels.length > 0) queuePatch(j.rels);
    await meta.delete('/__patch-todo');
  } catch { /* 패치 목록이 없거나 읽을 수 없으면 필요할 때 그대로 받는다 */ }
}

/** 이 기기의 서비스 워커 등록을 모두 풀고 dh-* 캐시를 지운다(localStorage 저장·계정은 건드리지 않는다) */
export async function uninstallPwa(): Promise<void> {
  try { const regs = await navigator.serviceWorker?.getRegistrations?.(); for (const r of regs ?? []) await r.unregister(); } catch { /* 못 풀면 다음 접속에서 */ }
  try { if (typeof caches !== 'undefined') for (const k of await caches.keys()) if (k.startsWith('dh-')) await caches.delete(k); } catch { /* 캐시가 없으면 그대로 */ }
}

export function startPwa(opts: { introDone?: Promise<void>; isSafe?: () => boolean; flush?: () => void } = {}): void {
  hooks = { isSafe: opts.isSafe ?? (() => true), flush: opts.flush ?? (() => {}) };
  const ua = navigator.userAgent || '';
  state.ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  state.inApp = !!(window as unknown as { __dhInApp?: { app?: string | null } }).__dhInApp?.app;
  state.installed = detectInstalled();
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); installEvent = e as InstallPromptEvent; state.canInstall = true; emit(); });
  window.addEventListener('appinstalled', () => { installEvent = null; state.canInstall = false; state.installed = true; emit(); });
  const params = new URLSearchParams(location.search);
  const dev = Boolean(import.meta.env.DEV);
  // 자동화 브라우저(navigator.webdriver)와 개발 서버에서는 ?pwa=1 로 켜야 등록한다(기존 QA 러너가 서비스 워커 캐시에 영향받지 않게). ?pwa=0 은 끈다
  const automated = navigator.webdriver === true;
  state.supported = 'serviceWorker' in navigator && (window.isSecureContext || location.hostname === 'localhost') && params.get('pwa') !== '0' && ((!dev && !automated) || params.get('pwa') === '1');
  emit();
  // 탈출구: ?pwa=0 으로 열면 이 기기의 서비스 워커 등록과 dh-* 캐시를 지운다(워커가 꼬였을 때 사용자가 스스로 풀 수 있게). 이번 접속은 이미 워커가 맡았을 수 있어 다음 접속부터 워커 없이 동작
  if (params.get('pwa') === '0') void uninstallPwa();
  // QA 훅(?qa·개발 서버 전용)
  if (params.has('qa') || dev) (window as unknown as { __DHPWA__: unknown }).__DHPWA__ = { state: () => ({ ...state }), apply: () => applyUpdateNow(), hasWaiting: () => !!reg?.waiting, applying: () => applying };
  if (!state.supported) return;
  const go = (): void => {
    navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).then((r) => {
      reg = r; state.registered = true; state.controlled = !!navigator.serviceWorker.controller; emit();
      watch(r);
      void navigator.serviceWorker.ready.then(() => { state.controlled = !!navigator.serviceWorker.controller; emit(); void readPatchTodo(); });
    }).catch(() => { state.supported = false; emit(); });
  };
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    state.controlled = true; emit();
    // 사용자가 "지금 적용"을 눌렀을 때만 새로고침한다(다른 탭이 적용한 경우 이 탭은 건드리지 않는다)
    // 활성화가 늦어져 그 사이 던전에 들어갔다면 새로고침하지 않는다(전투 중 강제 새로고침 금지, 새 버전은 다음 접속에 적용된다)
    if (applying) { applying = false; if (hooks.isSafe()) { hooks.flush(); location.reload(); } else holdRegionData(false); }
  });
  const start = (): void => {
    const run = (): void => { if ('requestIdleCallback' in window) (window as unknown as { requestIdleCallback(f: () => void, o: { timeout: number }): void }).requestIdleCallback(go, { timeout: 4000 }); else setTimeout(go, 1500); };
    if (document.readyState === 'complete') run(); else window.addEventListener('load', run, { once: true });
  };
  // 인트로가 걷힌 뒤(없으면 바로), 늦어도 10초 뒤
  if (opts.introDone) { let started = false; const once = (): void => { if (!started) { started = true; start(); } }; void opts.introDone.then(once); setTimeout(once, 10000); }
  else start();
}

/** 새 버전을 지금 적용한다. 마을·타이틀(안전한 장면)이 아니면 아무것도 하지 않는다 */
export function applyUpdateNow(): boolean {
  if (!state.updateReady || !reg?.waiting || !hooks.isSafe()) return false;
  hooks.flush();
  applying = true;
  // 새 워커가 30초 안에 일을 넘겨받지 못하면 "지금 적용"은 없던 일로 하고 다음 접속에 맡긴다(뒤늦은 controllerchange 로 갑자기 새로고침되지 않게)
  holdRegionData(true); // 새 요청을 멈춰 옛 워커가 빨리 넘겨주게 한다
  setTimeout(() => { if (applying) { applying = false; holdRegionData(false); } }, 30000);
  reg.waiting.postMessage({ type: 'SKIP_WAITING' });
  return true;
}

/** 앱 설치 창(지원하는 브라우저) */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!installEvent) return 'unavailable';
  const e = installEvent; installEvent = null; state.canInstall = false; emit();
  try { await e.prompt(); return (await e.userChoice).outcome; } catch { return 'unavailable'; }
}
